import { styled, SxProps, Theme, Typography } from "@mui/material";
import { TextArea } from "@volt_developers/components";
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";

import { TEST_IDS } from "../../constants";
import { Asterisk } from "../Asterisk";

export interface CommentProps<T> {
  maxLength: number;
  name: Path<T>;
  isRequired?: boolean;
  sx?: SxProps;
}

export function Comment<T extends FieldValues>({
  maxLength,
  name,
  sx,
  isRequired = false,
}: CommentProps<T>) {
  const { t } = useTranslation("common");
  const { control } = useFormContext<T>();
  const { field } = useController({
    name,
    control,
  });
  const comment = useWatch({ control, name });
  const count = maxLength - (comment?.length ?? 0);

  return (
    <Wrapper data-testid={TEST_IDS.comment} sx={sx}>
      <TitleWrapper>
        <Typography
          color="text.high"
          sx={{ mb: 1, textAlign: "left" }}
          variant="h7"
        >
          {t(COMMON_TRANSLATION_KEYS.comment)}
        </Typography>
        {isRequired && <Asterisk />}
      </TitleWrapper>
      <TextArea
        data-testid={TEST_IDS.getInput(TEST_IDS.comment)}
        maxRows={4}
        minRows={4}
        placeholder={t(COMMON_TRANSLATION_KEYS.typeHere)}
        sx={(theme) => getTextAreaStyles(theme, count)}
        {...field}
        value={field.value ?? ""}
      />
      <Typography
        color={count < 0 ? "failure.main" : "text.medium"}
        variant="body3"
        sx={{
          textAlign: "left",
          pb: 1.5,
        }}
      >
        {t(COMMON_TRANSLATION_KEYS.xYCharactersRemaining, {
          count,
          maxNumber: maxLength,
        })}
      </Typography>
    </Wrapper>
  );
}

function getTextAreaStyles(theme: Theme, count: number) {
  return {
    ...(count < 0 && {
      borderColor: theme.palette.failure.main,

      "&:focus-within": {
        borderColor: theme.palette.failure.main,
      },
    }),
  };
}

const TitleWrapper = styled("div")({
  display: "flex",
  flexDirection: "row",
});

const Wrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
});
